import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Copy, Check, AlertCircle, Wallet } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import Header from '../components/Header'

import { useUser } from '@/hooks/use-user'
import SwapForm from '@/components/SwapForm'
import { apiFetch } from '@/utils'
import { mutate } from 'swr'
import { useSellTokenBalance } from '@/hooks/use-sellTokenBalance'
import { SELL_TOKEN_ABBR } from '@/constants'
import { swapTokensWithMetaMask } from '@/utils/swapTokens'

const BalanceCard = ({
  name,
  balance,
  decimals = 7
}: {
  name: string
  balance: number
  decimals?: number
}) => {
  return (
    <Card className="p-3">
      <div className="text-sm text-muted-foreground">{name}</div>
      <div className="text-lg font-semibold">{balance.toFixed(decimals)}</div>
    </Card>
  )
}

function SwapPageContent() {
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null)
  const {
    isConnected,
    isConnectingWallet,
    walletAddress,
    balance: balanceSECH,
    balanceLoading: balanceSECHLoading,
    updateBalance: updateSECHBalance,
    connectWallet
  } = useUser()
  const { toast } = useToast()

  const { sellTokenBalance, isBalanceLoading, refetchSellTokenBalance } =
    useSellTokenBalance(walletAddress)

  const onSwap = async (fromTokenAmount: string, toTokenAmount: string) => {
    try {
      let transactionHash
      if (isConnected) {
        const transactionResult = await swapTokensWithMetaMask(
          fromTokenAmount,
          toTokenAmount,
          walletAddress
        )
        transactionHash = transactionResult.transactionHash
      }

      const response = await apiFetch('/v1/swap', {
        withCredentials: true,
        method: 'POST',
        body: JSON.stringify({
          userWalletAddress: walletAddress,
          fromTokenAmount,
          toTokenAmount,
          transactionHash
        })
      })

      if (response.ok) {
        toast({
          title: 'Swap Successful',
          description: 'Tokens have been swapped successfully'
        })
        // Triggering update balance
        await Promise.all([updateSECHBalance(), refetchSellTokenBalance()])
      } else {
        const result = await response?.json()

        toast({
          title: 'Swap Failed',
          description: `${result?.title ? `${result?.title}. ` : ''}${
            result?.details || result?.message || result
          }`,
          variant: 'destructive'
        })
      }
      mutate(`/v1/transactions/${walletAddress}`)
    } catch (error: any) {
      toast({
        title: error?.title || 'Error',
        description: `Failed to swap tokens. ${
          error?.details || error?.message
        }`,
        variant: 'destructive'
      })
    }
  }

  const copyToClipboard = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address)
      setCopiedAddress(address)
      toast({
        title: 'Address Copied',
        description: 'Address has been copied to clipboard'
      })
      setTimeout(() => setCopiedAddress(null), 2000)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy address',
        variant: 'destructive'
      })
    }
  }

  return (
    <>
      <Header />
      <div className="container mx-auto p-4 max-w-2xl">
        <Card className="p-6">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold">Token Swap</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Exchange {SELL_TOKEN_ABBR} to receive SECH tokens
            </p>
          </div>

          <SwapForm
            onSwap={onSwap}
            isConnectedWallet={isConnected}
            sellTokenBalance={sellTokenBalance}
            isBalanceLoading={isBalanceLoading}
          />

          {!isConnected && (
            <>
              <Alert variant="destructive" className="mt-4 p-3">
                <AlertDescription className="flex flex-row items-center justify-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  This feature is currently available only for MetaMask users
                  with a {SELL_TOKEN_ABBR} balance
                </AlertDescription>
              </Alert>

              <Button
                onClick={connectWallet}
                className="w-full mt-4"
                size="lg"
                disabled={isConnectingWallet}>
                {isConnectingWallet ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  'Connect MetaMask Wallet'
                )}
              </Button>
            </>
          )}
        </Card>

        {/* Connected Wallet Section */}
        <Card className="mt-6 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Your Wallet</h2>
            {!walletAddress && (
              <Button onClick={connectWallet}>
                <Wallet className="mr-2 h-4 w-4" />
                Connect Wallet
              </Button>
            )}
          </div>

          {walletAddress ? (
            <div className="space-y-4">
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-md font-medium">Wallet Address:</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(walletAddress as string)}>
                    {copiedAddress === walletAddress ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <code className="block w-full p-2 bg-background rounded text-sm break-all">
                  {walletAddress}
                </code>

                <div className="mt-4">
                  <h2 className="text-md font-semibold mb-2">Tokens Balance</h2>
                  {balanceSECHLoading || isBalanceLoading ? (
                    <div className="flex items-center h-20 justify-center">
                      <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                      Fetching balances...
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <>
                        <BalanceCard
                          name="SECH"
                          balance={balanceSECH}
                          decimals={3}
                        />
                        <BalanceCard
                          name={SELL_TOKEN_ABBR}
                          balance={sellTokenBalance}
                        />
                      </>
                    </div>
                  )}
                </div>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Make sure you have enough {SELL_TOKEN_ABBR} tokens in your
                  wallet before initiating a swap.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Please connect your wallet to receive SECH tokens.
              </AlertDescription>
            </Alert>
          )}
        </Card>
      </div>
    </>
  )
}


export default function SwapPage() {
  return <SwapPageContent />
}
